import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Badge, Button } from 'reactstrap';
import { useSort, SortTh } from '../ui/sortable';
import { formatBookingDate, type PickListItem } from '../../api/pickLists';
import { PickedInlineCell } from './PickedInlineCell';

const PAGE = 100;
const eur = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });

function renderPicked(picked: number, expected: number, isPostPicking: boolean) {
  if (picked === 0) {
    // bug_fixes_tpicking_development — a prelievo concluso (lista evasa/confermata)
    // uno "0 forzato" su N previsti va mostrato come "0/N" (inevaso, rosso), non
    // come trattino. Il trattino resta solo per le liste non ancora prelevate.
    return isPostPicking && expected > 0
      ? <span className="text-danger fw-semibold">0/{expected}</span>
      : <span className="text-muted">—</span>;
  }
  if (picked < expected) {
    return <span className="text-danger fw-semibold">{picked}/{expected}</span>;
  }
  return <span className="text-success">{picked}</span>;
}

export interface PickListItemsSectionProps {
  items: PickListItem[];
  customerKey?: { customer_code: string; recipient_code: number | null } | null;
  /**
   * SP-8 — quando la pick-list è `fulfilled` o `confirmed`, applichiamo il
   * "post-picking flow": le righe inevase (qty_picked < qty_required)
   * salgono in cima (zero pickup prima dei parziali) e ricevono la classe
   * `vp-row-partial` per evidenziarle visivamente. Default null = nessun
   * riordino (back-compat con consumer pre-SP-8).
   */
  pickListStatus?: string | null;
  /** edit-picked: abilita la modifica del Prel. (ruolo: warehouse-admin o platform-admin). */
  canEditPicked?: boolean;
  /**
   * Data di consegna a Book, o null se la lista non è ancora partita.
   *
   * È **il** vincolo alla correzione del Prel.: valorizzato, il campo si
   * congela. Non lo stato della lista — una *Evasa* non è ancora arrivata a
   * Book, ed è proprio lì che i conti si sistemano.
   */
  exportedAt?: string | null;
  onEditPicked?: (pliId: number, qtyPicked: number) => Promise<void>;
}

const POST_PICKING_STATUSES = new Set(['fulfilled', 'confirmed']);
/**
 * Specchio di `PickListItemPickedService::STATI_MODIFICABILI` (backend).
 *
 * ⚠️ Un solo stato, dal 30/09. Il 21/09 erano tre: l'allargamento rendeva
 * possibile correggere una riga mentre l'operatore prelevava le altre della
 * stessa lista. Se qui si aggiunge uno stato senza aggiungerlo anche al backend,
 * il campo compare e il salvataggio viene rifiutato.
 */
const STATI_MODIFICABILI = new Set(['fulfilled']);

function isPartialRow(it: PickListItem): boolean {
  return (it.qty_picked ?? 0) < it.qty_required;
}

function isZeroPickRow(it: PickListItem): boolean {
  return (it.qty_picked ?? 0) === 0;
}

export function PickListItemsSection({ items, customerKey, pickListStatus, canEditPicked, exportedAt, onEditPicked }: PickListItemsSectionProps) {
  // Quando si corregge la quantita' prelevata: **solo a lista Evasa, e solo se
  // non e' ancora partita per Book**. Rispecchia il backend, che resta
  // l'autorita': qui si decide solo se MOSTRARE il campo, non se la modifica e'
  // lecita.
  //
  // Sono DUE condizioni e proteggono da due cose diverse:
  //  - lo STATO (`fulfilled`) dal conflitto con l'operatore — a prelievo in
  //    corso la sua conferma sovrascriverebbe la correzione;
  //  - l'INVIO (`exportedAt`) dall'accordo rotto con Book — dopo la consegna il
  //    numero non raggiungerebbe piu' il file partito.
  //
  // La seconda non e' ridondante: fra l'invio e la consegna verificata lo stato
  // e' ancora `fulfilled`.
  const modificabile = canEditPicked
    && exportedAt == null
    && STATI_MODIFICABILI.has(pickListStatus ?? '');

  const { t } = useTranslation(['picklist', 'common']);
  const [page, setPage] = useState(0);
  // evasioni-correzioni-parziale: riga in modifica del prelevato (null = modal chiuso).
  const sort = useSort('location_code', 'asc');

  useEffect(() => { setPage(0); }, [customerKey]);

  const byCustomer = useMemo(() => {
    if (customerKey == null) return items;
    const { customer_code, recipient_code } = customerKey;
    return items.filter((it) =>
      it.customer_code === customer_code
      && (it.recipient_code ?? null) === (recipient_code ?? null));
  }, [items, customerKey]);

  // SP-8b — i filtri articolo (EAN/titolo/editore/ubicazioni/tipologia/data)
  // sono stati rimossi dal dettaglio: EAN13 + range data sono ora filtri
  // GLOBALI in sidebar (filtrano la LISTA). Qui resta solo l'ordinamento
  // colonne e il filtro per cliente selezionato (radio in-accordion).
  const filtered = useMemo(
    () => sort.sortRows(byCustomer, (it, col) => (it as unknown as Record<string, unknown>)[col]),
    [byCustomer, sort],
  );

  // SP-8 — flow post-picking: zero pickup prima dei parziali, parziali prima
  // dei completati. Stable sort preserva l'ordine `filtered` all'interno di
  // ciascun gruppo (utente puo' ancora cliccare gli header per il sort
  // secondario, vediamo l'effetto dentro ogni gruppo).
  const isPostPickingFlow = pickListStatus != null && POST_PICKING_STATUSES.has(pickListStatus);
  const ordered = useMemo(() => {
    if (!isPostPickingFlow) return filtered;
    const bucket = (it: PickListItem): number => {
      if (isZeroPickRow(it)) return 0;
      if (isPartialRow(it)) return 1;
      return 2; // completata
    };
    return [...filtered]
      .map((it, i) => ({ it, i, b: bucket(it) }))
      .sort((a, b) => (a.b - b.b) || (a.i - b.i))
      .map((x) => x.it);
  }, [filtered, isPostPickingFlow]);

  const pageItems = ordered.slice(page * PAGE, (page + 1) * PAGE);
  const totalPages = Math.max(1, Math.ceil(ordered.length / PAGE));

  return (
    <section className="pl-acc-section pl-acc-section--items">
      <h2 className="vp-label mb-2">{t('items.title_count', { count: byCustomer.length })}</h2>

      {filtered.length === 0 ? (
        <p className="text-muted">{t('items.empty')}</p>
      ) : (
        <>
          <div className="table-responsive">
          <Table className="table-hover align-middle table-striped mb-0">
            <thead>
              {/* SP-8g §9 — ordinamento su TUTTE le colonne (tri-stato). */}
              <tr>
                <SortTh label={t('items.ean13')} sortKey="ean13" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
                <SortTh label={t('items.item_title')} sortKey="title" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
                <SortTh label={t('items.publisher')} sortKey="publisher" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
                <SortTh label={t('items.item_type')} sortKey="item_type" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
                <SortTh label={t('items.qty_ordered')} className="text-end" sortKey="qty_ordered" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
                <SortTh label={t('items.qty_assigned')} className="text-end" sortKey="qty_assigned" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
                <SortTh label={t('items.qty_picked')} title={t('items.qty_picked_tooltip')} className="text-end" sortKey="qty_picked" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
                <SortTh label={t('items.price')} className="text-end" sortKey="price" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
                <SortTh label={t('items.stock')} className="text-end" sortKey="stock_qty" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
                <SortTh label={t('items.location_1')} sortKey="location_code" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
                <SortTh label={t('items.location_2')} sortKey="location_code_2" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
                <SortTh label={t('items.booking_date')} sortKey="booking_date" col={sort.col} dir={sort.dir} onToggle={sort.toggle} />
              </tr>
            </thead>
            <tbody>
              {pageItems.map((it) => {
                // SP-11b: una riga è "inevaso" (rosso + evidenziata) quando la
                // riga STESSA è stata processata short — `status==='partial'`
                // (include lo zero-pick / forza 0 con task done) — oppure, per
                // back-compat, quando la lista è in post-picking. Così un forza 0
                // appare subito (0/N rosso) anche durante AWAITING_MISSION_CLOSE,
                // prima che la lista diventi `fulfilled`.
                // Riga ESCLUSA dal prelievo: non e' ne' prelevata ne' mancante.
                // Va distinta a colpo d'occhio da un inevaso — sono due fatti
                // opposti, e dargli lo stesso aspetto rosso farebbe cercare
                // merce che nessuno doveva prelevare.
                const esclusa = it.status === 'excluded' || it.excluded_from_picking === true;
                const showInevaso = !esclusa
                  && (it.status === 'partial' || (isPostPickingFlow && isPartialRow(it)));
                return (
                <tr
                  key={it.pick_list_item_id ?? it.seq}
                  className={esclusa ? 'vp-row-excluded' : showInevaso ? 'vp-row-partial' : undefined}
                  title={esclusa
                    ? `Ubicazione ${it.location_code ?? ''} esclusa dal prelievo: non viene comunicata all'operatore`
                    : showInevaso ? t('items.partial_row_tooltip') : undefined}
                >
                  <td className="font-monospace vp-code">{it.ean13 ?? it.item_external_id ?? '—'}</td>
                  <td>{it.title ?? describe(it) ?? '—'}</td>
                  <td>{it.publisher ?? '—'}</td>
                  <td>
                    {it.item_type
                      ? <Badge className={it.item_type === 'S' ? 'bg-warning-subtle text-warning' : 'bg-info-subtle text-info'}>
                          {it.item_type === 'S' ? t('items.item_type_S') : it.item_type === 'V' ? t('items.item_type_V') : it.item_type}
                        </Badge>
                      : '—'}
                  </td>
                  <td className="text-end">{it.qty_ordered ?? '—'}</td>
                  <td className="text-end">{it.qty_assigned ?? it.qty_required}</td>
                  {/* Prel.: riferimento = qty_required, come il backend (range
                      0..qty_required) e tutta la logica inevasi/conferma/Book.
                      qty_assigned è il dato Book "Qta assegnata", solo esposto. */}
                  <td className="text-end">
                    {modificabile && onEditPicked && it.pick_list_item_id != null
                      && (it.tasks?.length ?? 0) <= 1
                      ? <PickedInlineCell
                          qtyPicked={it.qty_picked ?? 0}
                          qtyRequired={it.qty_required}
                          editable
                          inevaso={showInevaso}
                          ariaLabel={t('items.picked_edit.cell_aria')}
                          onSave={(qty) => onEditPicked(it.pick_list_item_id!, qty)}
                        />
                      : (modificabile && (it.tasks?.length ?? 0) > 1
                          ? <span title={t('items.picked_multitask_tooltip')}>{renderPicked(it.qty_picked ?? 0, it.qty_required, showInevaso)}</span>
                          : renderPicked(it.qty_picked ?? 0, it.qty_required, showInevaso))}
                  </td>
                  <td className="text-end">{it.price != null ? eur.format(it.price) : '—'}</td>
                  <td className="text-end">{it.stock_qty ?? '—'}</td>
                  <td className="font-monospace">{it.location_code ?? '—'}</td>
                  <td className="font-monospace">{it.location_code_2 ?? '—'}</td>
                  <td>{formatBookingDate(it.booking_date)}</td>
                </tr>
                );
              })}
            </tbody>
          </Table>
          </div>
          <div className="d-flex gap-2 align-items-center mt-2">
            <Button size="sm" color="light" disabled={page === 0} onClick={() => setPage(page - 1)}>{t('pagination.prev')}</Button>
            <span className="text-muted small">{t('pagination.page_of', { page: page + 1, total: totalPages })}</span>
            <Button size="sm" color="light" disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>{t('pagination.next')}</Button>
          </div>
        </>
      )}

    </section>
  );
}

// Fallback product description from attrs_snapshot when `title` is absent (legacy rows).
function describe(it: PickListItem): string | null {
  const a = it.attrs_snapshot;
  if (!a || typeof a !== 'object') return null;
  for (const key of ['titolo', 'title', 'descrizione', 'name', 'description']) {
    const v = (a as Record<string, unknown>)[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}
